import { Tuning } from '../types';
import { getNoteFrequency } from './frequencies';

/**
 * Generate tuning from note names
 */
function createTuning(
  name: string,
  notes: string[],
  description?: string,
  referenceFreq = 440
): Tuning {
  return {
    name,
    notes,
    frequencies: notes.map(note => getNoteFrequency(note, referenceFreq)),
    description,
  };
}

/**
 * Guitar tunings (6-string)
 */
export const GUITAR_TUNINGS: Record<string, Tuning> = {
  standard: createTuning(
    'Standard',
    ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'],
    'Standard guitar tuning (E A D G B E)'
  ),

  dropD: createTuning(
    'Drop D',
    ['D2', 'A2', 'D3', 'G3', 'B3', 'E4'],
    'Drop D tuning - low E string tuned down to D'
  ),

  openG: createTuning(
    'Open G',
    ['D2', 'G2', 'D3', 'G3', 'B3', 'D4'],
    'Open G tuning - popular for slide guitar'
  ),

  dadgad: createTuning(
    'DADGAD',
    ['D2', 'A2', 'D3', 'G3', 'A3', 'D4'],
    'DADGAD tuning - popular in Celtic music'
  ),

  halfStepDown: createTuning(
    'Half Step Down',
    ['Eb2', 'Ab2', 'Db3', 'Gb3', 'Bb3', 'Eb4'],
    'All strings tuned down a half step'
  ),

  fullStepDown: createTuning(
    'Full Step Down',
    ['D2', 'G2', 'C3', 'F3', 'A3', 'D4'],
    'All strings tuned down a full step'
  ),

  dropC: createTuning(
    'Drop C',
    ['C2', 'G2', 'C3', 'F3', 'A3', 'D4'],
    'Drop C tuning - popular in metal'
  ),

  openD: createTuning(
    'Open D',
    ['D2', 'A2', 'D3', 'F#3', 'A3', 'D4'],
    'Open D tuning - another slide guitar favorite'
  ),

  openE: createTuning(
    'Open E',
    ['E2', 'B2', 'E3', 'G#3', 'B3', 'E4'],
    'Open E tuning - used by slide guitarists'
  ),

  openA: createTuning(
    'Open A',
    ['E2', 'A2', 'E3', 'A3', 'C#4', 'E4'],
    'Open A tuning'
  ),
};

/**
 * Bass guitar tunings (4-string and 5-string)
 */
export const BASS_TUNINGS: Record<string, Tuning> = {
  standard4: createTuning(
    'Standard 4-String',
    ['E1', 'A1', 'D2', 'G2'],
    'Standard 4-string bass tuning'
  ),

  standard5: createTuning(
    'Standard 5-String',
    ['B0', 'E1', 'A1', 'D2', 'G2'],
    'Standard 5-string bass tuning with low B'
  ),

  dropD4: createTuning(
    'Drop D (4-String)',
    ['D1', 'A1', 'D2', 'G2'],
    'Drop D tuning for 4-string bass'
  ),

  halfStepDown4: createTuning(
    'Half Step Down (4-String)',
    ['Eb1', 'Ab1', 'Db2', 'Gb2'],
    '4-string bass tuned down a half step'
  ),
};

/**
 * Ukulele tunings
 */
export const UKULELE_TUNINGS: Record<string, Tuning> = {
  standardC: createTuning(
    'Standard C (Soprano/Concert/Tenor)',
    ['G4', 'C4', 'E4', 'A4'],
    'Standard ukulele tuning with re-entrant G'
  ),

  standardCLowG: createTuning(
    'Standard C (Low G)',
    ['G3', 'C4', 'E4', 'A4'],
    'Standard ukulele tuning with low G'
  ),

  baritone: createTuning(
    'Baritone',
    ['D3', 'G3', 'B3', 'E4'],
    'Baritone ukulele tuning'
  ),

  dTuning: createTuning(
    'D Tuning',
    ['A4', 'D4', 'F#4', 'B4'],
    'D tuning - whole step up from C'
  ),
};

/**
 * Banjo tunings (5-string)
 */
export const BANJO_TUNINGS: Record<string, Tuning> = {
  openG: createTuning(
    'Open G (Standard)',
    ['G4', 'D3', 'G3', 'B3', 'D4'],
    'Standard 5-string banjo tuning (5th string is G4)'
  ),

  doubleC: createTuning(
    'Double C',
    ['G4', 'C3', 'G3', 'C4', 'D4'],
    'Double C tuning for banjo'
  ),

  openD: createTuning(
    'Open D',
    ['A4', 'D3', 'F#3', 'A3', 'D4'],
    'Open D tuning for banjo'
  ),
};
